import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { heatColor, HEAT_RAMP, HEAT_EMPTY } from '../constants';
import { Footnote } from '../styles';

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

const Grid = styled.div`
  display: grid;
  grid-template-columns: 34px repeat(${HOURS.length}, 1fr);
  gap: 3px;
  align-items: center;
`;

const DowLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #9CA3AF;
  text-align: right;
  padding-right: 4px;
`;

const HourLabel = styled.div`
  font-size: 9px;
  color: #9CA3AF;
  text-align: center;
`;

const Cell = styled.div`
  aspect-ratio: 1;
  border-radius: 3px;
  background: ${({ $bg }) => $bg};
`;

const LegendWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6B7280;
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: ${({ $color }) => $color};
`;

export default function EntryHeatmapCard({ slice }) {
  const { t } = useTranslation();
  const data = slice.data;

  const dowNames = [
    t('expenseDashboard.heatmap.mon'), t('expenseDashboard.heatmap.tue'), t('expenseDashboard.heatmap.wed'),
    t('expenseDashboard.heatmap.thu'), t('expenseDashboard.heatmap.fri'), t('expenseDashboard.heatmap.sat'),
    t('expenseDashboard.heatmap.sun'),
  ];

  const cellMap = useMemo(() => {
    const map = new Map();
    (data?.cells || []).forEach((c) => map.set(`${c.dow}-${c.hour}`, c.count));
    return map;
  }, [data]);

  return (
    <DashboardCard
      cardKey="masraf-giris-yogunlugu"
      title={t('expenseDashboard.heatmap.title')}
      meta={t('expenseDashboard.heatmap.meta')}
      span={4}
      spanMd={3}
      loading={slice.loading}
      empty={!data || (data.cells || []).length === 0}
      emptyText={t('expenseDashboard.heatmap.empty')}
      headerExtra={(
        <LegendWrap data-export-keep>
          {t('expenseDashboard.heatmap.legendLow')}
          {HEAT_RAMP.map((c) => <Swatch key={c} $color={c} />)}
          {t('expenseDashboard.heatmap.legendHigh')}
        </LegendWrap>
      )}
    >
      <div>
        <Grid>
          <span />
          {HOURS.map((h) => <HourLabel key={h}>{String(h).padStart(2, '0')}</HourLabel>)}
          {dowNames.map((name, dowIdx) => (
            <React.Fragment key={name}>
              <DowLabel>{name}</DowLabel>
              {HOURS.map((h) => {
                const count = cellMap.get(`${dowIdx + 1}-${h}`) || 0;
                return (
                  <Cell
                    key={h}
                    $bg={count ? heatColor(count, data.maxCount) : HEAT_EMPTY}
                    title={`${name} ${String(h).padStart(2, '0')}:00 — ${t('expenseDashboard.heatmap.entries', { count })}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
        <Footnote>{t('expenseDashboard.heatmap.note')}</Footnote>
      </div>
    </DashboardCard>
  );
}
